import React, { useState, useRef, useEffect } from 'react';

function RegistrationForm({ onRegistration }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    whomToMeet: '',
    purposeOfVisit: '',
    aadharNumber: '',
    notes: '',
  });

  const [touched, setTouched] = useState({
    email: false,
    phone: false,
    aadharNumber: false,
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: '',
    aadharNumber: '',
  });

  // Photo Capture State
  const [photoData, setPhotoData] = useState('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState('');

  // ID Proof Upload & Camera State
  const [idProofData, setIdProofData] = useState('');
  const [idProofName, setIdProofName] = useState('');
  const [idProofError, setIdProofError] = useState('');
  const [idProofSource, setIdProofSource] = useState(''); // 'camera' | 'file' | ''
  const [isIdCameraOpen, setIsIdCameraOpen] = useState(false);
  const [isIdCameraStarting, setIsIdCameraStarting] = useState(false);
  const [idCameraError, setIdCameraError] = useState('');
  const [idCameraFacingMode, setIdCameraFacingMode] = useState('environment'); // environment (back) or user (front)

  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const fileInputRef = useRef(null);
  const idProofInputRef = useRef(null);
  const idVideoRef = useRef(null);
  const idMediaStreamRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Stop all camera tracks on unmount
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (idMediaStreamRef.current) {
        idMediaStreamRef.current.getTracks().forEach((track) => track.stop());
        idMediaStreamRef.current = null;
      }
    };
  }, []);

  // When visitor photo camera opens and video element is rendered, attach stream
  useEffect(() => {
    if (isCameraOpen && videoRef.current && mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
    }
  }, [isCameraOpen]);

  // When ID proof camera opens and video element is rendered, attach stream
  useEffect(() => {
    if (isIdCameraOpen && idVideoRef.current && idMediaStreamRef.current) {
      idVideoRef.current.srcObject = idMediaStreamRef.current;
    }
  }, [isIdCameraOpen]);

  // Start webcam for visitor photo
  const startCamera = async () => {
    stopIdCamera();
    setCameraError('');
    setIsCameraStarting(true);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access is not supported by your browser or requires HTTPS / localhost.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user',
        },
        audio: false,
      });

      mediaStreamRef.current = stream;
      setIsCameraOpen(true);
    } catch (err) {
      console.error('Camera access error:', err);
      let msg = 'Unable to access camera. Please allow camera permissions in your browser.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Camera permission was denied. Please allow camera access in your browser.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        msg = 'No camera device found on this computer/laptop.';
      }
      setCameraError(msg);
    } finally {
      setIsCameraStarting(false);
    }
  };

  // Stop webcam
  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  // Capture snapshot from webcam
  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    setPhotoData(dataUrl);
    stopCamera();
    setCameraError('');
  };

  // Retake photo
  const handleRetake = () => {
    setPhotoData('');
    startCamera();
  };

  // Start webcam for ID Proof
  const startIdCamera = async (facing = idCameraFacingMode) => {
    stopCamera();
    stopIdCamera();
    setIdCameraError('');
    setIdProofError('');
    setIsIdCameraStarting(true);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access is not supported by your browser or requires HTTPS / localhost.');
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facing,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (constraintErr) {
        console.warn('Facing mode constraint failed, falling back to default video device:', constraintErr);
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      idMediaStreamRef.current = stream;
      setIsIdCameraOpen(true);
    } catch (err) {
      console.error('ID camera access error:', err);
      let msg = 'Unable to access camera for ID proof. Please allow camera permissions in your browser.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Camera permission was denied. Please allow camera access in your browser.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        msg = 'No camera device found on this computer/laptop.';
      }
      setIdCameraError(msg);
    } finally {
      setIsIdCameraStarting(false);
    }
  };

  // Stop webcam for ID Proof
  const stopIdCamera = () => {
    if (idMediaStreamRef.current) {
      idMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      idMediaStreamRef.current = null;
    }
    if (idVideoRef.current) {
      idVideoRef.current.srcObject = null;
    }
    setIsIdCameraOpen(false);
  };

  // Switch between front and back camera for ID card capture
  const switchIdCamera = () => {
    const nextMode = idCameraFacingMode === 'environment' ? 'user' : 'environment';
    setIdCameraFacingMode(nextMode);
    startIdCamera(nextMode);
  };

  // Capture snapshot of ID Card from webcam
  const captureIdPhoto = () => {
    if (!idVideoRef.current) return;
    const video = idVideoRef.current;
    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setIdProofData(dataUrl);
    setIdProofName(`id_card_captured_${Date.now()}.jpg`);
    setIdProofSource('camera');
    stopIdCamera();
    setIdCameraError('');
    setIdProofError('');
  };

  // Retake ID proof via camera
  const handleRetakeIdProof = () => {
    setIdProofData('');
    startIdCamera(idCameraFacingMode);
  };

  // Fallback photo file upload
  const handleFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setCameraError('Please select a valid image file (JPEG, PNG).');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotoData(event.target.result);
      stopCamera();
      setCameraError('');
    };
    reader.readAsDataURL(file);
  };

  // ID Proof file upload (Images & PDFs)
  const handleIdProofUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setIdProofError('');
    setIdCameraError('');
    stopIdCamera();

    if (file.size > 10 * 1024 * 1024) {
      setIdProofError('ID proof file size must be less than 10MB.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const isAllowed = allowedTypes.includes(file.type) || file.name.match(/\.(jpg|jpeg|png|webp|pdf)$/i);
    if (!isAllowed) {
      setIdProofError('Please upload an image (JPG, PNG) or PDF document for ID proof.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setIdProofData(event.target.result);
      setIdProofName(file.name);
      setIdProofSource('file');
      setIdProofError('');
    };
    reader.readAsDataURL(file);
  };

  const removeIdProof = () => {
    stopIdCamera();
    setIdProofData('');
    setIdProofName('');
    setIdProofError('');
    setIdCameraError('');
    setIdProofSource('');
    if (idProofInputRef.current) {
      idProofInputRef.current.value = '';
    }
  };

  // Validation functions (Only name, whomToMeet, purposeOfVisit are mandatory)
  const validateEmail = (email) => {
    if (!email || !email.trim()) return ''; // Optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address (e.g. name@example.com)';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone || !phone.trim()) return ''; // Optional
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!phoneRegex.test(cleanPhone)) {
      const digitsOnly = cleanPhone.replace(/\+/g, '');
      if (digitsOnly.length < 10) {
        return `Mobile number must contain at least 10 digits (currently ${digitsOnly.length})`;
      }
      if (digitsOnly.length > 15) {
        return 'Mobile number cannot exceed 15 digits';
      }
      return 'Please enter a valid mobile number (10-15 digits)';
    }
    return '';
  };

  const validateAadhar = (aadhar) => {
    if (!aadhar || !aadhar.trim()) return ''; // Optional
    const clean = aadhar.replace(/[\s-]/g, '');
    if (!/^\d{12}$/.test(clean)) {
      return 'Aadhaar number must be a 12-digit number (e.g. 1234 5678 9012)';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'email' && touched.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (name === 'phone' && touched.phone) {
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    } else if (name === 'aadharNumber' && touched.aadharNumber) {
      setErrors((prev) => ({ ...prev, aadharNumber: validateAadhar(value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (name === 'phone') {
      setErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    } else if (name === 'aadharNumber') {
      setErrors((prev) => ({ ...prev, aadharNumber: validateAadhar(value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Mark fields as touched
    setTouched({ email: true, phone: true, aadharNumber: true });

    // Validate optional fields if filled
    const emailErr = validateEmail(formData.email);
    const phoneErr = validatePhone(formData.phone);
    const aadharErr = validateAadhar(formData.aadharNumber);

    setErrors({
      email: emailErr,
      phone: phoneErr,
      aadharNumber: aadharErr,
    });

    if (emailErr || phoneErr || aadharErr) {
      setFormError(emailErr || phoneErr || aadharErr);
      return;
    }

    // Mandatory fields check: ONLY Full Name, Whom to Meet, Purpose of Visit
    if (!formData.name.trim() || !formData.whomToMeet.trim() || !formData.purposeOfVisit) {
      setFormError('Please fill in all mandatory fields: Full Name, Whom to Meet, and Purpose of Visit.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      ...formData,
      photoData: photoData || '',
      idProofData: idProofData || '',
    };

    const success = await onRegistration(payload);

    if (success) {
      // Reset form & media state
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        whomToMeet: '',
        purposeOfVisit: '',
        aadharNumber: '',
        notes: '',
      });
      setPhotoData('');
      stopCamera();
      removeIdProof();
      setTouched({ email: false, phone: false, aadharNumber: false });
      setErrors({ email: '', phone: '', aadharNumber: '' });
      setFormError('');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="card">
      <h2 style={{ marginBottom: '20px', color: '#333' }}>New Visitor Registration</h2>

      {formError && (
        <div className="form-alert-error">
          ⚠️ {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter visitor's full name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address (Optional)</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter email address (e.g. name@company.com)"
            className={touched.email && errors.email ? 'input-error' : ''}
          />
          {touched.email && errors.email && (
            <div className="field-error-message">⚠️ {errors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Mobile Number (Optional)</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter mobile number (e.g. 9876543210)"
            className={touched.phone && errors.phone ? 'input-error' : ''}
          />
          {touched.phone && errors.phone && (
            <div className="field-error-message">⚠️ {errors.phone}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="company">Company (Optional)</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Enter company name (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="whomToMeet">Whom to Meet *</label>
          <input
            type="text"
            id="whomToMeet"
            name="whomToMeet"
            value={formData.whomToMeet}
            onChange={handleChange}
            placeholder="Enter person or department to meet (e.g. John Doe, HR)"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="purposeOfVisit">Purpose of Visit *</label>
          <select
            id="purposeOfVisit"
            name="purposeOfVisit"
            value={formData.purposeOfVisit}
            onChange={handleChange}
            required
          >
            <option value="">Select purpose...</option>
            <option value="Meeting">Meeting</option>
            <option value="Interview">Interview</option>
            <option value="Delivery">Delivery</option>
            <option value="Support">Support</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="aadharNumber">Aadhaar Number (Optional)</label>
          <input
            type="text"
            id="aadharNumber"
            name="aadharNumber"
            value={formData.aadharNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter 12-digit Aadhaar number (e.g. 1234 5678 9012)"
            maxLength="14"
            className={touched.aadharNumber && errors.aadharNumber ? 'input-error' : ''}
          />
          {touched.aadharNumber && errors.aadharNumber && (
            <div className="field-error-message">⚠️ {errors.aadharNumber}</div>
          )}
        </div>

        {/* Live Webcam Photo Capture Section */}
        <div className="form-group photo-capture-section">
          <label className="photo-section-label">
            <span>📷 Visitor Photo</span>
            <span className="photo-section-hint">Take live photo from laptop camera for gate pass & S3</span>
          </label>

          {cameraError && (
            <div className="photo-alert-error">
              ⚠️ {cameraError}
            </div>
          )}

          {/* If photo captured: show preview & retake option */}
          {photoData ? (
            <div className="photo-preview-card">
              <div className="photo-preview-wrapper">
                <img src={photoData} alt="Captured Visitor" className="photo-preview-img" />
                <span className="photo-badge-success">✓ Photo Ready</span>
              </div>
              <div className="photo-preview-details">
                <div className="photo-preview-title">Photo Captured Successfully</div>
                <div className="photo-preview-subtitle">
                  This photo will be uploaded to AWS S3 (<code>visiter-gatepass/{'{visitorId}'}/photo.jpg</code>) and printed on the pass.
                </div>
                <div className="photo-actions-row">
                  <button
                    type="button"
                    className="btn-photo-action btn-photo-retake"
                    onClick={handleRetake}
                  >
                    🔄 Retake Photo
                  </button>
                  <button
                    type="button"
                    className="btn-photo-action btn-photo-remove"
                    onClick={() => setPhotoData('')}
                  >
                    ✕ Remove
                  </button>
                </div>
              </div>
            </div>
          ) : isCameraOpen ? (
            /* Live Camera Viewfinder */
            <div className="camera-viewfinder-container">
              <div className="camera-header-status">
                <span className="camera-live-dot"></span>
                <span>Camera Active — Align visitor inside frame</span>
              </div>
              <div className="camera-video-wrapper">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="camera-video-element"
                />
                <div className="camera-face-guide">
                  <div className="camera-face-oval"></div>
                </div>
              </div>
              <div className="camera-controls-bar">
                <button
                  type="button"
                  className="btn-camera-capture"
                  onClick={capturePhoto}
                >
                  📸 Capture Photo
                </button>
                <button
                  type="button"
                  className="btn-camera-cancel"
                  onClick={stopCamera}
                >
                  ✕ Close Camera
                </button>
              </div>
            </div>
          ) : (
            /* Idle: Open Camera or Upload */
            <div className="photo-idle-box">
              <div className="photo-idle-content">
                <div className="photo-idle-icon">📸</div>
                <div className="photo-idle-text">
                  <strong>Take Visitor Photo with Webcam</strong>
                  <p>Click below to activate your laptop camera and capture visitor photo</p>
                </div>
              </div>
              <div className="photo-idle-actions">
                <button
                  type="button"
                  className="btn-open-camera"
                  onClick={startCamera}
                  disabled={isCameraStarting}
                >
                  {isCameraStarting ? 'Starting Camera...' : '📷 Open Camera'}
                </button>
                <button
                  type="button"
                  className="btn-upload-fallback"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  📁 Upload Photo File
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Visitor ID Proof Section (Camera Capture & File Upload) */}
        <div className="form-group photo-capture-section id-proof-capture-section">
          <label className="photo-section-label">
            <span>🪪 Upload or Capture ID Proof (Aadhaar / Voter ID / PAN / Driving License)</span>
            <span className="photo-section-hint">Option to take photo with camera or upload file (Stored in S3: visiter-gatepass/{'{visitorId}'}/id_proof)</span>
          </label>

          {idProofError && (
            <div className="photo-alert-error">
              ⚠️ {idProofError}
            </div>
          )}

          {idCameraError && (
            <div className="photo-alert-error">
              ⚠️ {idCameraError}
            </div>
          )}

          {idProofData ? (
            /* ID Proof Attached Preview */
            <div className="photo-preview-card id-proof-preview-card">
              <div className="id-proof-preview-media">
                {idProofData.startsWith('data:image') || idProofName.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                  <div className="photo-preview-wrapper id-proof-preview-wrapper">
                    <img src={idProofData} alt="Captured ID Proof" className="photo-preview-img id-proof-thumb-img" />
                    <span className="photo-badge-success">
                      {idProofSource === 'camera' ? '✓ Camera Scan' : '✓ ID Ready'}
                    </span>
                  </div>
                ) : (
                  <div className="id-proof-preview-icon">
                    {idProofName.toLowerCase().endsWith('.pdf') ? '📑' : '🖼️'}
                  </div>
                )}
              </div>
              <div className="photo-preview-details">
                <div className="photo-preview-title" style={{ wordBreak: 'break-all' }}>
                  {idProofName || 'ID Proof Document'}
                </div>
                <div className="photo-preview-subtitle">
                  ✓ ID Proof attached {idProofSource === 'camera' ? 'via Camera' : 'via File Upload'}. Stored in visitor S3 folder: <code>visiter-gatepass/{'{visitorId}'}/id_proof</code>
                </div>
                <div className="photo-actions-row">
                  <button
                    type="button"
                    className="btn-photo-action btn-photo-retake"
                    onClick={handleRetakeIdProof}
                  >
                    🔄 Retake with Camera
                  </button>
                  <button
                    type="button"
                    className="btn-photo-action btn-idproof-change"
                    onClick={() => idProofInputRef.current && idProofInputRef.current.click()}
                  >
                    📁 Change File
                  </button>
                  <button
                    type="button"
                    className="btn-photo-action btn-photo-remove"
                    onClick={removeIdProof}
                  >
                    ✕ Remove ID Proof
                  </button>
                </div>
              </div>
            </div>
          ) : isIdCameraOpen ? (
            /* Live ID Card Camera Viewfinder */
            <div className="camera-viewfinder-container">
              <div className="camera-header-status">
                <span className="camera-live-dot"></span>
                <span>ID Card Camera Active — Align card inside frame</span>
              </div>
              <div className="camera-video-wrapper camera-id-video-wrapper">
                <video
                  ref={idVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="camera-video-element"
                />
                <div className="camera-id-guide">
                  <div className="camera-id-rect">
                    <span className="camera-id-corner top-left"></span>
                    <span className="camera-id-corner top-right"></span>
                    <span className="camera-id-corner bottom-left"></span>
                    <span className="camera-id-corner bottom-right"></span>
                    <div className="camera-id-label">
                      💳 PLACE ID CARD (AADHAAR / PAN / VOTER ID / DL) HERE
                    </div>
                  </div>
                </div>
              </div>
              <div className="camera-controls-bar">
                <button
                  type="button"
                  className="btn-camera-capture"
                  onClick={captureIdPhoto}
                >
                  📸 Capture ID Card
                </button>
                <button
                  type="button"
                  className="btn-camera-flip"
                  onClick={switchIdCamera}
                  title="Switch front/back camera"
                >
                  🔄 Switch Camera
                </button>
                <button
                  type="button"
                  className="btn-camera-cancel"
                  onClick={stopIdCamera}
                >
                  ✕ Close Camera
                </button>
              </div>
            </div>
          ) : (
            /* Idle: Options for Camera Capture or File Upload */
            <div className="photo-idle-box id-proof-drop-box">
              <div className="photo-idle-content">
                <div className="photo-idle-icon">🪪</div>
                <div className="photo-idle-text">
                  <strong>Visitor ID Proof (Aadhaar / Voter ID / PAN / Driving License)</strong>
                  <p>Take a photo directly with camera or upload document file (JPG, PNG, PDF up to 10MB)</p>
                </div>
              </div>
              <div className="photo-idle-actions">
                <button
                  type="button"
                  className="btn-open-camera btn-id-camera"
                  onClick={() => startIdCamera()}
                  disabled={isIdCameraStarting}
                >
                  {isIdCameraStarting ? 'Starting Camera...' : '📷 Capture from Camera'}
                </button>
                <button
                  type="button"
                  className="btn-upload-fallback"
                  onClick={() => idProofInputRef.current && idProofInputRef.current.click()}
                >
                  📁 Upload ID File (PDF/Image)
                </button>
                <input
                  type="file"
                  ref={idProofInputRef}
                  accept="image/jpeg,image/png,image/webp,application/pdf,.pdf,.jpg,.jpeg,.png"
                  onChange={handleIdProofUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="notes">Additional Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any additional information (optional)"
          ></textarea>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Registering...' : 'Register Visitor'}
        </button>
      </form>
    </div>
  );
}

export default RegistrationForm;
